/**
 * InvoiceInfoForm Component
 * Fatura bilgileri formu
 */

import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InvoiceSection, SectionTitle } from '../styled/InvoiceStyledComponents';
import { AppDatePicker } from '../../../../common/AppDatePicker';

interface InvoiceInfoFormProps {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  exchangeRate: string;
  currencies: Array<{code: string; name?: string; active?: boolean;}>;
  onInvoiceNumberChange: (value: string) => void;
  onInvoiceDateChange: (date: string) => void;
  onDueDateChange: (date: string) => void;
  onCurrencyChange: (value: string) => void;
  onExchangeRateChange: (value: string) => void;
  disabled?: boolean;
  isEditMode?: boolean;
  /** Belge turu: basliklar buna gore Fatura/Teklif/Siparis/Irsaliye yazar. Varsayilan: invoice */
  mode?: 'invoice' | 'offer' | 'waybill' | 'order';
}

const DOC_NOUNS: Record<NonNullable<InvoiceInfoFormProps['mode']>, { tr: string; en: string }> = {
  invoice: { tr: 'Fatura', en: 'Invoice' },
  offer: { tr: 'Teklif', en: 'Offer' },
  order: { tr: 'Sipariş', en: 'Order' },
  waybill: { tr: 'İrsaliye', en: 'Waybill' },
};

export const InvoiceInfoForm: React.FC<InvoiceInfoFormProps> = ({
  invoiceNumber,
  invoiceDate,
  dueDate,
  currency,
  exchangeRate,
  currencies,
  onInvoiceNumberChange,
  onInvoiceDateChange,
  onDueDateChange,
  onCurrencyChange,
  onExchangeRateChange,
  disabled = false,
  isEditMode = false,
  mode = 'invoice',
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const noun = DOC_NOUNS[mode] || DOC_NOUNS.invoice;

  return (
    <InvoiceSection>
      <SectionTitle variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
        {tt(`${noun.tr} Bilgileri`, `${noun.en} Information`)}
      </SectionTitle>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={tt(`${noun.tr} No`, `${noun.en} No`)}
            value={invoiceNumber}
            onChange={(e) => onInvoiceNumberChange(e.target.value)}
            size="small"
            required
            disabled={disabled}
            InputProps={{
              readOnly: isEditMode,
            }}
            helperText={isEditMode ? tt(`${noun.tr} numarası düzenlenemez`, `${noun.en} number cannot be edited`) : ''}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <AppDatePicker
            fullWidth
            label={tt(`${noun.tr} Tarihi`, `${noun.en} Date`)}
            value={invoiceDate}
            onChange={onInvoiceDateChange}
            disabled={disabled}
            size="small"
            required
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <AppDatePicker
            fullWidth
            label={tt('Vade Tarihi', 'Due Date')}
            value={dueDate}
            onChange={onDueDateChange}
            disabled={disabled}
            size="small"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>{tt('Para Birimi', 'Currency')}</InputLabel>
            <Select
              value={currency}
              label={tt('Para Birimi', 'Currency')}
              onChange={(e) => onCurrencyChange(e.target.value)}
            >
              {currencies.map((curr) => (
                <MenuItem key={curr.code} value={curr.code}>
                  {curr.code}{curr.name ? ` - ${curr.name}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {currency !== 'TRY' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={tt('Döviz Kuru', 'Exchange Rate')}
              value={exchangeRate}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  onExchangeRateChange(value);
                }
              }}
              size="small"
              required
              disabled={disabled}
              type="text"
              inputMode="decimal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">
                      {currency}/TRY
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={
                exchangeRate && parseFloat(exchangeRate) > 0
                  ? `1 ${currency} = ${parseFloat(exchangeRate).toFixed(4)} TRY`
                  : tt('Döviz kurunu girin', 'Enter exchange rate')
              }
            />
          </Grid>
        )}
      </Grid>
    </InvoiceSection>
  );
};

